import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import PaymentModal from "../components/PaymentModal";
import {
  HiOutlineCalendar,
  HiOutlineUser,
  HiX,
  HiChevronLeft,
  HiChevronRight,
  HiOutlineClock,
} from "react-icons/hi";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getLoggedInUser } from "../utils/authUser";

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const NurseAppointment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { nurse } = location.state || {};
  
  const [loading, setLoading] = useState(false);
  const [nurses, setNurses] = useState([]);
  const [filteredNurses, setFilteredNurses] = useState([]);
  const [services, setServices] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentDateIndex, setCurrentDateIndex] = useState(0);
  const [selectedNurse, setSelectedNurse] = useState(null);
  const [selectedNurseData, setSelectedNurseData] = useState(null); // Full nurse data from API (includes work_schedule)
  const [selectedService, setSelectedService] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingAppointmentData, setPendingAppointmentData] = useState(null);
  const [nurseBookedTimesForDate, setNurseBookedTimesForDate] = useState([]);
  const [loadingBookedSlots, setLoadingBookedSlots] = useState(false);
  const [formData, setFormData] = useState({
    patientName: "",
    patientPhone: "",
    patientAge: "",
    patientGender: "",
    appointmentType: "Nursing",
    appointmentDate: "",
    shift: "",
    appointmentTime: "",
    address: "",
    location: "",
    nurse_id: "",
    customer_id: "",
    service_id: "",
    status: "Scheduled",
  });

  const parseWorkSchedule = (ws) => {
    if (!ws) return null;
    if (typeof ws === "object") return ws;
    if (typeof ws === "string") {
      try {
        return JSON.parse(ws);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const getDaySchedule = (ws, dayKey) => {
    if (!ws || !dayKey) return null;
    const lower = dayKey.toLowerCase();
    const cap = dayKey.charAt(0).toUpperCase() + dayKey.slice(1);
    return ws[lower] ?? ws[cap] ?? null;
  };

  const formatTime = (hour, minute) => {
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`;
  };

  const normalizeTime = (t) => {
    if (!t) return "";
    const s = String(t).trim();
    const parts = s.split(/[:\s]/);
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  // Format date as local YYYY-MM-DD (avoid toISOString() which uses UTC and can shift the day)
  const toLocalDateString = (date) => {
    if (!date || !(date instanceof Date)) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  // Only current time and future slots allowed. Slot must be at least 30 min from now.
  const isSlotInPast = (slotTimeStr, forDate) => {
    if (!forDate || !slotTimeStr) return false;
    const today = new Date();
    if (forDate.getDate() !== today.getDate() || forDate.getMonth() !== today.getMonth() || forDate.getFullYear() !== today.getFullYear()) return false;
    const [h, m] = String(slotTimeStr).split(/[:\s]/).map(Number);
    const slotMinutes = (h || 0) * 60 + (m || 0);
    const nowMinutes = today.getHours() * 60 + today.getMinutes();
    const MIN_BUFFER_MINUTES = 30; // Slot must be at least 30 min from now
    return slotMinutes <= nowMinutes + MIN_BUFFER_MINUTES;
  };

  const generateAvailableDates = (workSchedule) => {
    const ws = parseWorkSchedule(workSchedule);
    if (!ws || typeof ws !== "object") return [];
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dayKey = DAY_NAMES[date.getDay()];
      const daySchedule = getDaySchedule(ws, dayKey);
      const hasEnabledShift = Array.isArray(daySchedule?.shifts) && daySchedule.shifts.some((s) => s.enabled !== false);
      if (daySchedule?.enabled && hasEnabledShift) {
        dates.push(date);
      }
    }
    return dates;
  };

  const generateTimeSlotsFromSchedule = (workSchedule, forDate) => {
    const ws = parseWorkSchedule(workSchedule);
    if (!ws || !forDate) return { morning: [], afternoon: [], evening: [] };
    const dayKey = DAY_NAMES[forDate.getDay()];
    const daySchedule = getDaySchedule(ws, dayKey);
    if (!daySchedule?.enabled || !Array.isArray(daySchedule.shifts) || daySchedule.shifts.length === 0) {
      return { morning: [], afternoon: [], evening: [] };
    }
    const morning = [];
    const afternoon = [];
    const evening = [];
    const enabledShifts = daySchedule.shifts.filter((s) => s.enabled !== false);
    enabledShifts.forEach((shift) => {
      const start = (shift.start_time || "08:00").trim().split(":").map(Number);
      const end = (shift.end_time || "17:00").trim().split(":").map(Number);
      let currentHour = start[0] || 8;
      let currentMinute = start[1] || 0;
      const endHour = end[0] || 17;
      const endMinute = end[1] || 0;
      while (currentHour < endHour || (currentHour === endHour && currentMinute < endMinute)) {
        const time = `${String(currentHour).padStart(2, "0")}:${String(currentMinute).padStart(2, "0")}`;
        const display = formatTime(currentHour, currentMinute);
        const slot = { time, display, available: true };
        if (currentHour < 12) morning.push(slot);
        else if (currentHour < 17) afternoon.push(slot);
        else evening.push(slot);
        currentMinute += 30;
        if (currentMinute >= 60) {
          currentMinute = 0;
          currentHour++;
        }
      }
    });
    return { morning, afternoon, evening };
  };

  const availableDates = useMemo(
    () => generateAvailableDates(selectedNurseData?.work_schedule ?? selectedNurse?.work_schedule),
    [selectedNurseData?.work_schedule, selectedNurse?.work_schedule]
  );

  useEffect(() => {
    const nurseId = formData.nurse_id ? parseInt(formData.nurse_id) : null;
    const dateStr = selectedDate ? toLocalDateString(selectedDate) : formData.appointmentDate;
    if (!nurseId || !dateStr) {
      setNurseBookedTimesForDate([]);
      return;
    }
    const fetchBookedSlots = async () => {
      setLoadingBookedSlots(true);
      try {
        const res = await axios.get("https://app.caaficare.so/api/nurses_appointment", {
          params: { nurse_id: nurseId, date: dateStr, limit: 200 },
        });
        const appointments = res?.data?.data || [];
        const times = appointments
          .filter((a) => (a.status || "").toLowerCase() !== "cancelled")
          .map((a) => (a.appointmentTime ? String(a.appointmentTime).trim() : ""))
          .filter(Boolean);
        setNurseBookedTimesForDate(times);
      } catch (e) {
        console.error("Error fetching nurse booked slots:", e);
        setNurseBookedTimesForDate([]);
      } finally {
        setLoadingBookedSlots(false);
      }
    };
    fetchBookedSlots();
  }, [formData.nurse_id, formData.appointmentDate, selectedDate]);

  const bookedSet = useMemo(
    () => new Set(nurseBookedTimesForDate.map(normalizeTime).filter(Boolean)),
    [nurseBookedTimesForDate]
  );

  const timeSlots = useMemo(() => {
    const nurseForSchedule = selectedNurseData ?? selectedNurse;
    if (!nurseForSchedule || !selectedDate) return { morning: [], afternoon: [], evening: [] };
    const raw = generateTimeSlotsFromSchedule(nurseForSchedule.work_schedule, selectedDate);
    const filterSlot = (slot) => {
      if (bookedSet.has(normalizeTime(slot.time))) return false;
      if (isSlotInPast(slot.time, selectedDate)) return false;
      return true;
    };
    return {
      morning: (raw.morning || []).filter(filterSlot),
      afternoon: (raw.afternoon || []).filter(filterSlot),
      evening: (raw.evening || []).filter(filterSlot),
    };
  }, [selectedNurseData, selectedNurse, selectedDate, bookedSet]);

  const getAvailableSlotsCount = (date) => {
    const nurseForSchedule = selectedNurseData ?? selectedNurse;
    if (!date || !nurseForSchedule) return null;
    if (selectedDate && date.getTime() === selectedDate.getTime()) {
      return timeSlots.morning.length + timeSlots.afternoon.length + timeSlots.evening.length;
    }
    const raw = generateTimeSlotsFromSchedule(nurseForSchedule.work_schedule, date);
    const allSlots = [...(raw.morning || []), ...(raw.afternoon || []), ...(raw.evening || [])];
    const today = new Date();
    const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
    const filtered = isToday ? allSlots.filter((s) => !isSlotInPast(s.time, date)) : allSlots;
    return filtered.length;
  };

  // Format date display
  const formatDateDisplay = (date, index) => {
    if (index === 0) return "Today";
    if (index === 1) return "Tomorrow";
    
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`;
  };

  // Pre-fill form if nurse is passed from NursesList
  useEffect(() => {
    if (nurse) {
      setFormData((prev) => ({
        ...prev,
        nurse_id: nurse.id.toString(),
        location: nurse.district || nurse.city || nurse.location || "",
      }));
      setSelectedNurse(nurse);
    }
  }, [nurse]);

  // Fetch full nurse data (including work_schedule) when nurse is selected - ensures correct schedule from API
  useEffect(() => {
    const nurseId = formData.nurse_id ? parseInt(formData.nurse_id) : null;
    if (!nurseId) {
      setSelectedNurseData(null);
      return;
    }
    const fetchNurseData = async () => {
      try {
        const res = await axios.get(`https://app.caaficare.so/api/nurses/${nurseId}`);
        if (res.data?.success && res.data.data) {
          setSelectedNurseData(res.data.data);
        } else {
          setSelectedNurseData(null);
        }
      } catch (err) {
        console.error("Error fetching nurse data:", err);
        setSelectedNurseData(null);
      }
    };
    fetchNurseData();
  }, [formData.nurse_id]);

  // Data Fetching
  useEffect(() => {
    const checkAuthAndFetch = async () => {
      const loggedInUser = getLoggedInUser();

      if (!loggedInUser?.id) {
        toast.error("Please login to book an appointment");
        navigate("/login");
        return;
      }

      setFormData((prev) => ({ ...prev, customer_id: loggedInUser.id }));

      setLoading(true);
      try {
        const [nursesRes, servicesRes] = await Promise.all([
          axios.get("https://app.caaficare.so/api/nurse?status=Active"),
          axios.get("https://app.caaficare.so/api/services"),
        ]);
        setNurses(nursesRes.data.data || []);
        setFilteredNurses(nursesRes.data.data || []);
        setServices(servicesRes.data || []);
      } catch (error) {
        toast.error("Failed to load clinical data");
      } finally {
        setLoading(false);
      }
    };

    checkAuthAndFetch();
  }, [navigate]);

  // Filter Logic
  useEffect(() => {
    const filtered = formData.location
      ? nurses.filter((n) =>
          n.location?.toLowerCase().includes(formData.location.toLowerCase()),
        )
      : nurses;
    setFilteredNurses(filtered);
    setSelectedService(
      services.find((s) => s.id === parseInt(formData.service_id)),
    );
  }, [
    formData.location,
    formData.nurse_id,
    formData.service_id,
    nurses,
    services,
  ]);

  // Clear date/time when nurse changes
  const prevNurseIdRef = useRef(formData.nurse_id);
  useEffect(() => {
    if (prevNurseIdRef.current !== formData.nurse_id) {
      prevNurseIdRef.current = formData.nurse_id;
      setSelectedDate(null);
      setSelectedTime(null);
      setCurrentDateIndex(0);
      setFormData((prev) => ({ ...prev, appointmentDate: "", appointmentTime: "", shift: "" }));
    }
  }, [formData.nurse_id]);

  // Clear selected time when date changes (DoctorAppointment pattern)
  const prevDateRef = useRef(selectedDate);
  useEffect(() => {
    if (prevDateRef.current !== selectedDate) {
      prevDateRef.current = selectedDate;
      setSelectedTime(null);
      setFormData((prev) => ({ ...prev, appointmentTime: "", shift: "" }));
    }
  }, [selectedDate]);

  // Clear selected time if it becomes booked or is now in the past (DoctorAppointment pattern)
  useEffect(() => {
    const current = formData.appointmentTime;
    if (!current || !selectedDate) return;
    if (bookedSet.has(normalizeTime(current)) || isSlotInPast(current, selectedDate)) {
      setFormData((prev) => ({ ...prev, appointmentTime: "", shift: "" }));
      setSelectedTime(null);
    }
  }, [nurseBookedTimesForDate, selectedDate]);

  // Keep selectedNurse in sync for display (name, qualifications) - prefer fetched data, fallback to list
  useEffect(() => {
    if (selectedNurseData) {
      setSelectedNurse(selectedNurseData);
    } else if (formData.nurse_id) {
      const nurse = nurses.find((n) => n.id === parseInt(formData.nurse_id));
      if (nurse) setSelectedNurse(nurse);
      // Else keep existing selectedNurse (e.g. from NursesList state) until fetch completes
    } else {
      setSelectedNurse(null);
    }
  }, [selectedNurseData, formData.nurse_id, nurses]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateSelect = (date, index) => {
    setSelectedDate(date);
    setCurrentDateIndex(index);
    setSelectedTime(null);
    const dateString = toLocalDateString(date);
    setFormData((prev) => ({ ...prev, appointmentDate: dateString, appointmentTime: "", shift: "" }));
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    let shiftVal = "";
    if (timeSlots.morning.some((s) => s.time === time)) shiftVal = "Morning";
    else if (timeSlots.afternoon.some((s) => s.time === time)) shiftVal = "Afternoon";
    else if (timeSlots.evening.some((s) => s.time === time)) shiftVal = "Evening";
    setFormData((prev) => ({ ...prev, appointmentTime: time, shift: shiftVal }));
  };

  const navigateDates = (direction) => {
    if (direction === "prev" && currentDateIndex > 0) {
      setCurrentDateIndex(currentDateIndex - 1);
    } else if (direction === "next" && currentDateIndex < availableDates.length - 3) {
      setCurrentDateIndex(currentDateIndex + 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.service_id || !formData.nurse_id) {
      toast.warn("Please select a service and a nurse");
      return;
    }
    if (!selectedDate || !selectedTime) {
      toast.warn("Please select a date and time");
      return;
    }

    // Reject past date or time (safety net - compare calendar date in local timezone)
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const selectedLocal = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    if (selectedLocal.getTime() < todayLocal.getTime()) {
      toast.error("Cannot book appointment for a past date. Please select a future date.");
      return;
    }
    if (selectedLocal.getTime() === todayLocal.getTime() && isSlotInPast(selectedTime, selectedDate)) {
      toast.error("This time slot has passed. Please select a current or future time.");
      setSelectedTime(null);
      setFormData((prev) => ({ ...prev, appointmentTime: "", shift: "" }));
      return;
    }

    // Show payment modal instead of submitting directly
    setPendingAppointmentData(formData);
    setShowPaymentModal(true);
  };

  // Handle payment completion - actually submit the appointment
  const handlePaymentComplete = async (paymentMethodName, merchantNumber) => {
    if (!pendingAppointmentData) return;

    setIsSubmitting(true);
    try {
      const payload = {
        ...pendingAppointmentData,
        ...(paymentMethodName && { payment_method: paymentMethodName }),
        ...(merchantNumber && { payment_merchant_number: merchantNumber }),
      };
      const response = await axios.post(
        "https://app.caaficare.so/api/nurses_appointment",
        payload,
      );
      if (response.data.success) {
        setIsSuccess(true);
        setShowPaymentModal(false);
        setPendingAppointmentData(null);
        setTimeout(() => {
          navigate("/");
        }, 3000);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Booking failed. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get appointment price from selected service
  const appointmentPrice = selectedService?.price || "0";

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        onAppointmentClick={() => navigate("/appointment")}
        onNurseAppointmentClick={() => navigate("/nurse-appointment")}
        onApplyNurseClick={() => {}}
        onApplyDoctorClick={() => {}}
      />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {isSuccess ? (
            <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-xl p-12 text-center">
              <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-5xl mx-auto mb-6 animate-bounce">
                ✓
              </div>
              <h2 className="text-4xl font-black text-gray-800 mb-4">
                Request Sent!
              </h2>
              <p className="text-gray-500 text-lg mb-6">
                Your nursing appointment request has been submitted
                successfully.
              </p>
              <p className="text-sm text-gray-400">
                Redirecting you to home page...
              </p>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                {/* Main Form Section */}
                <div className="lg:col-span-2 p-8 lg:p-12">
                  <div className="mb-8">
                    <h1 className="text-3xl font-black text-gray-800 tracking-tight mb-2">
                      Nursing Appointment
                    </h1>
                    <p className="text-gray-500">
                      Book professional in-home nursing care services
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <FormRow label="Service">
                      <select
                        name="service_id"
                        value={formData.service_id}
                        onChange={handleChange}
                        required
                        className="input-field"
                      >
                        <option value="">Choose Nursing Service</option>
                        {services.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.serviceName} - ${s.price}
                          </option>
                        ))}
                      </select>
                    </FormRow>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                      <div className="space-y-4">
                        <label className="text-[10px] font-bold text-blue-500 uppercase ml-1">
                          Patient Info
                        </label>
                        <input
                          type="text"
                          name="patientName"
                          placeholder="Full Name"
                          value={formData.patientName}
                          onChange={handleChange}
                          required
                          className="input-field"
                        />
                        <input
                          type="tel"
                          name="patientPhone"
                          placeholder="WhatsApp Number"
                          value={formData.patientPhone}
                          onChange={handleChange}
                          required
                          className="input-field"
                        />
                      </div>
                      <div className="space-y-4 pt-6">
                        <div className="flex gap-2">
                          <input
                            type="number"
                            name="patientAge"
                            placeholder="Age"
                            value={formData.patientAge}
                            onChange={handleChange}
                            required
                            className="w-1/2 input-field"
                          />
                          <select
                            name="patientGender"
                            value={formData.patientGender}
                            onChange={handleChange}
                            required
                            className="w-1/2 input-field"
                          >
                            <option value="">Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                          </select>
                        </div>
                        <input
                          type="text"
                          name="location"
                          placeholder="Search District/City"
                          value={formData.location}
                          onChange={handleChange}
                          className="input-field"
                        />
                      </div>
                    </div>

                    <FormRow label="Choose Nurse">
                      <select
                        name="nurse_id"
                        value={formData.nurse_id}
                        onChange={handleChange}
                        required
                        className="input-field"
                      >
                        <option value="">
                          {loading
                            ? "Fetching clinical staff..."
                            : "Available Nurses"}
                        </option>
                        {filteredNurses.map((n) => (
                          <option key={n.id} value={n.id}>
                            {n.name} ({n.district || "General"})
                          </option>
                        ))}
                      </select>
                    </FormRow>

                    {/* Schedule Selection - from nurse work_schedule */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-blue-600">
                        <HiOutlineCalendar className="w-5 h-5" />
                        <span className="text-[10px] font-black uppercase tracking-widest">
                          Select Date & Time
                        </span>
                      </div>

                      {!formData.nurse_id ? (
                        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                          <p className="text-amber-800 text-sm">Please select a nurse first to see available dates and times.</p>
                        </div>
                      ) : availableDates.length === 0 ? (
                        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6">
                          <p className="text-yellow-800 text-sm font-medium">
                            No available dates found for this nurse. The nurse may not have a work schedule configured, or no days are enabled.
                          </p>
                        </div>
                      ) : (
                        <div className="bg-white rounded-2xl border-2 border-gray-100 p-6">
                          <div className="flex items-center justify-between mb-4">
                            <button
                              type="button"
                              onClick={() => navigateDates("prev")}
                              disabled={currentDateIndex === 0}
                              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                              <HiChevronLeft className="w-5 h-5 text-blue-600" />
                            </button>

                            <div className="flex items-center gap-3 flex-1 justify-center">
                              {availableDates.slice(currentDateIndex, currentDateIndex + 3).map((date, idx) => {
                                const actualIndex = currentDateIndex + idx;
                                const isSelected = selectedDate && selectedDate.getTime() === date.getTime();
                                return (
                                  <button
                                    key={actualIndex}
                                    type="button"
                                    onClick={() => handleDateSelect(date, actualIndex)}
                                    className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all min-w-[120px] ${
                                      isSelected
                                        ? "border-blue-500 bg-blue-50"
                                        : "border-gray-200 hover:border-blue-300"
                                    }`}
                                  >
                                    <span className={`text-sm font-bold mb-1 ${isSelected ? "text-blue-600" : "text-gray-700"}`}>
                                      {formatDateDisplay(date, actualIndex)}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {getAvailableSlotsCount(date) != null ? `${getAvailableSlotsCount(date)} Available` : "Select"}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>

                            <button
                              type="button"
                              onClick={() => navigateDates("next")}
                              disabled={currentDateIndex >= availableDates.length - 3}
                              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                              <HiChevronRight className="w-5 h-5 text-blue-600" />
                            </button>
                          </div>

                          {selectedDate && (
                            <div className="mt-6 max-h-96 overflow-y-auto">
                              {loadingBookedSlots && (
                                <p className="text-sm text-gray-500 mb-4 flex items-center gap-2">Checking availability...</p>
                              )}
                              {timeSlots.morning.length > 0 && (
                                <div className="mb-6">
                                  <div className="flex items-center gap-2 mb-4">
                                    <HiOutlineClock className="w-4 h-4 text-amber-500" />
                                    <h4 className="text-sm font-bold text-gray-700">Morning</h4>
                                  </div>
                                  <div className="grid grid-cols-4 gap-2">
                                    {timeSlots.morning.map((slot, idx) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        onClick={() => handleTimeSelect(slot.time)}
                                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                                          selectedTime === slot.time
                                            ? "border-blue-500 bg-blue-50 text-blue-700"
                                            : "border-gray-200 hover:border-blue-300 text-gray-700"
                                        }`}
                                      >
                                        {slot.display}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {timeSlots.afternoon.length > 0 && (
                                <div className="mb-6">
                                  <div className="flex items-center gap-2 mb-4">
                                    <HiOutlineClock className="w-4 h-4 text-amber-500" />
                                    <h4 className="text-sm font-bold text-gray-700">Afternoon</h4>
                                  </div>
                                  <div className="grid grid-cols-4 gap-2">
                                    {timeSlots.afternoon.map((slot, idx) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        onClick={() => handleTimeSelect(slot.time)}
                                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                                          selectedTime === slot.time
                                            ? "border-blue-500 bg-blue-50 text-blue-700"
                                            : "border-gray-200 hover:border-blue-300 text-gray-700"
                                        }`}
                                      >
                                        {slot.display}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {timeSlots.evening.length > 0 && (
                                <div>
                                  <div className="flex items-center gap-2 mb-4">
                                    <HiOutlineClock className="w-4 h-4 text-blue-500" />
                                    <h4 className="text-sm font-bold text-gray-700">Evening</h4>
                                  </div>
                                  <div className="grid grid-cols-4 gap-2">
                                    {timeSlots.evening.map((slot, idx) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        onClick={() => handleTimeSelect(slot.time)}
                                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                                          selectedTime === slot.time
                                            ? "border-blue-500 bg-blue-50 text-blue-700"
                                            : "border-gray-200 hover:border-blue-300 text-gray-700"
                                        }`}
                                      >
                                        {slot.display}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {timeSlots.morning.length === 0 && timeSlots.afternoon.length === 0 && timeSlots.evening.length === 0 && !loadingBookedSlots && (
                                <div className="text-center py-8 text-gray-500">
                                  <p className="text-sm">No time slots available for this date</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <textarea
                      name="address"
                      placeholder="Full Residential Address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      rows="3"
                      className="input-field"
                    />

                    <button
                      type="submit"
                      disabled={isSubmitting || !selectedDate || !selectedTime}
                      className="w-full py-4 bg-[#6CA9F5] text-white rounded-2xl font-bold shadow-lg shadow-blue-200 hover:bg-blue-600 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                    >
                      {isSubmitting ? "Processing..." : "Confirm Nurse Booking"}
                    </button>
                  </form>
                </div>

                {/* Sidebar Summary */}
                <div className="bg-slate-50 p-8 lg:p-12 border-t lg:border-t-0 lg:border-l border-gray-100">
                  <h3 className="font-bold text-gray-800 mb-6 border-b pb-4 text-lg">
                    Booking Details
                  </h3>

                  {selectedService && (
                    <div className="mb-6">
                      <p className="text-[10px] font-black text-gray-400 uppercase mb-2">
                        Service Cost
                      </p>
                      <p className="text-4xl font-black text-[#6CA9F5]">
                        ${selectedService.price}
                      </p>
                    </div>
                  )}

                  {selectedNurse && (
                    <div className="p-4 bg-white rounded-2xl border border-blue-100 shadow-sm">
                      <p className="text-[10px] font-black text-blue-500 uppercase mb-2">
                        Primary Nurse
                      </p>
                      <p className="font-bold text-gray-700 text-lg">
                        {selectedNurse.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedNurse.qualifications ||
                          "Certified Professional"}
                      </p>
                    </div>
                  )}

                  {!selectedService && !selectedNurse && (
                    <div className="text-center py-12 text-gray-400">
                      <p className="text-sm">Fill in the form to see details</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

    

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setPendingAppointmentData(null);
        }}
        appointmentPrice={appointmentPrice}
        onPaymentComplete={handlePaymentComplete}
      />

      <style jsx>{`
        .input-field {
          width: 100%;
          padding: 0.875rem 1.25rem;
          border-radius: 1rem;
          border: 1px solid #eef2f6;
          background: #ffffff;
          outline: none;
          font-size: 0.875rem;
          transition: all 0.2s;
        }
        .input-field:focus {
          border-color: #6ca9f5;
          background: white;
          box-shadow: 0 0 0 4px rgba(108, 169, 245, 0.1);
        }
      `}</style>
    </div>
  );
};

const FormRow = ({ label, children }) => (
  <div className="flex flex-col md:flex-row md:items-center gap-2 mb-4">
    <label className="md:w-1/4 text-xs font-black uppercase text-gray-400 ml-1">
      {label}
    </label>
    <div className="md:w-3/4">{children}</div>
  </div>
);

export default NurseAppointment;